import { useInViewport } from "ahooks";
import React, { memo, useRef } from "react";
import { Timeslot } from "types";
import { SkeletonLine } from "../../Common/Skeleton/TableSkeleton";
import { CapacityBalancePrivacyDisplay } from "./CapacityBalancePrivacyDisplay";
import { GuestWaiverVoucherDisplay } from "./GuestWaiverVoucherDisplay";
import { GuideInventoryAddOnDisplay } from "./GuideInventoryAddOnDisplay";
import { NotesButtonsDisplay } from "./NotesButtonsDisplay";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { useProduct } from "@/hooks/resources/useProduct";

interface ExpandedTimeslotDisplayProps {
    timeslot: Timeslot;
}

export const ExpandedTimeslotDisplay = memo(({ timeslot }: ExpandedTimeslotDisplayProps) => {
    // Track whether the experience is in the user's view.
    const viewRef = useRef(null);
    const [inViewport] = useInViewport(viewRef);
    return (
        <div ref={viewRef} className="flex flex-grow flex-col">
            {inViewport ? (
                <>
                    <ExperienceTitleDisplay productId={timeslot.product} />
                    <div className="flex items-start justify-between">
                        <CapacityBalancePrivacyDisplay timeslot={timeslot} />
                        <GuestWaiverVoucherDisplay timeslot={timeslot} />
                        <GuideInventoryAddOnDisplay timeslot={timeslot} />
                        <NotesButtonsDisplay timeslot={timeslot} />
                    </div>
                </>
            ) : (
                <TimeslotSkeleton />
            )}
        </div>
    );
});

export const BasicTimeslotDisplay = memo(({ timeslot }: ExpandedTimeslotDisplayProps) => {
    return (
        <div className="flex flex-grow flex-col">
            <ExperienceTitleDisplay productId={timeslot.product} />
            <div className="flex items-start justify-between">
                <CapacityBalancePrivacyDisplay timeslot={timeslot} />
                <GuestWaiverVoucherDisplay timeslot={timeslot} />
                <GuideInventoryAddOnDisplay timeslot={timeslot} />
                <NotesButtonsDisplay timeslot={timeslot} />
            </div>
        </div>
    );
});

interface ExperienceTitleProps {
    productId: ID;
}

export const ExperienceTitleDisplay = memo(({ productId }: ExperienceTitleProps) => {
    const { data: product } = useProduct(productId);
    return (
        <NoTranslate
            as="div"
            title={product?.name?.trim()}
            className="ui-dashboard-product h-4.5 w-4/5 font-bold leading-p1 line-clamp-1"
        >
            {product?.name?.trim() ?? <SkeletonLine className="w-1/2" />}
        </NoTranslate>
    );
});

const TimeslotSkeleton = () => {
    return (
        <div className="flex h-full justify-between">
            <SkeletonLine className="h-full w-1/2" />
            <div className="flex w-36 flex-row justify-around	">
                <SkeletonLine className="h-full w-1/3" />
                <SkeletonLine className="h-full w-1/3" />
            </div>
        </div>
    );
};
